import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Paper,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import apiService from "../../service/Api";
import { RootState } from "../../store/Index";
import { login, clearTwoFaUsername, User } from "../../store/slice/AuthSlice";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/common/ShowToast";
import backgroundImage from "../../assets/backgroundImage.jpg";
import { motion } from "framer-motion";
import { clearTheme, setTheme } from "../../store/slice/ThemeSlice";
import { IThemeState } from "../../types/theme";
import { easeInOut } from "framer-motion";

const Verify2FA = () => {
  const [totpCode, setTotpCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const twoFaUsername = useSelector(
    (state: RootState) => state.auth.twoFaUsername
  );

  const muzukuru = "../../assets/muzukuru.png";
  const muzukuruLogoToUse = reduxTheme.logo || muzukuru;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpCode || !/^\d{6}$/.test(totpCode)) {
      showErrorToast(
        "Please enter a valid 6-digit 2FA code.",
        "2fa-code-error"
      );
      return;
    }

    if (!twoFaUsername) {
      showErrorToast(
        "No username found. Please log in again.",
        "2fa-username-error"
      );
      navigate("/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access_token: string;
        refresh_token: string;
        user: User;
        is_2fa_enabled: boolean;
        message: string;
      }>("/api/authentication/2fa/login-verify/", {
        username: twoFaUsername,
        totp_code: totpCode,
      });

      // Store tokens and update auth state
      dispatch(
        login({
          user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          is_2fa_enabled: response.data.is_2fa_enabled,
        })
      );

      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
      dispatch(clearTwoFaUsername()); // Clear twoFaUsername from Redux

      // Fetch and set theme
      dispatch(clearTheme());
      try {
        const themeResponse = await apiService.get<IThemeState>(
          `/api/companies/theme/${encodeURIComponent(
            response.data.user.username
          )}`
        );
        dispatch(setTheme(themeResponse.data));
      } catch (themeErr) {
        console.error("Failed to fetch theme:", themeErr);
      }

      showSuccessToast("2FA verified successfully!");
      navigate("/dashboard");
    } catch (err: any) {
      console.error("2FA verification error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      let errorMessage = "Invalid 2FA code. Please try again.";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      showErrorToast(errorMessage, "2fa-verify-error");
      navigate("/login");
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Left Column - Enhanced Image Background */}
      <Grid
        size={{ xs: false, md: 6 }}
        sx={{
          position: "relative",
          display: { xs: "none", md: "block" },
          background: `linear-gradient(135deg, rgba(97, 68, 56, 0.8) 0%, rgba(194, 147, 99, 0.6) 100%), url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(45deg, rgba(97, 68, 56, 0.1) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        {/* Floating geometric shapes */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            right: "10%",
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "rgba(194, 147, 99, 0.2)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 90, 180],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "70%",
            right: "20%",
            width: "50px",
            height: "50px",
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            zIndex: 2,
          }}
          component={motion.div}
          animate={{
            x: [0, 20, 0],
            rotate: [0, -90, -180],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: easeInOut,
          }}
        />
        {/* Enhanced Logo Section */}
        <Box
          sx={{
            position: "absolute",
            bottom: 40,
            left: 40,
            display: "flex",
            alignItems: "center",
            zIndex: 5,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            whileHover={{ scale: 1.05 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: "16px",
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <motion.img
                alt="Muzukuru logo"
                width="165px"
                height="70px"
                src={muzukuruLogoToUse}
                style={{ cursor: "pointer" }}
              />
            </Paper>
          </motion.div>
        </Box>
        {/* Welcome text overlay */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            zIndex: 3,
            color: "white",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textShadow: "0 4px 20px rgba(0,0,0,0.3)",
                background:
                  "linear-gradient(45deg, #fff 30%, rgba(194, 147, 99, 1) 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Secure Your Account
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                fontWeight: 300,
              }}
            >
              Verify your identity to continue
            </Typography>
          </motion.div>
        </Box>
      </Grid>
      {/* Right Column - Enhanced 2FA Form */}
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, md: 8 },
          py: { xs: 4, md: 0 },
          background: `linear-gradient(135deg, ${
            reduxTheme.neutral_colors?.[900] || "#fafafa"
          } 0%, ${reduxTheme.neutral_colors?.[800] || "#f5f5f5"} 100%)`,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 80%, rgba(194, 147, 99, 0.05) 0%, transparent 50%)",
            zIndex: 1,
          },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ width: "100%", maxWidth: "480px", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px)",
              padding: { xs: "24px", sm: "32px", md: "40px" },
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, ${
                  reduxTheme.primary_colors?.[400] || "#614438"
                } 0%, ${reduxTheme.primary_colors?.[300] || "#C29363"} 100%)`,
              },
            }}
            component={motion.div}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <Box component="form" onSubmit={handleSubmit}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textAlign: "center",
                    marginBottom: "8px",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Verify 2FA
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="body1"
                  sx={{
                    color: reduxTheme.neutral_colors?.[500] || "#666",
                    textAlign: "center",
                    marginBottom: "24px",
                    fontSize: "16px",
                    lineHeight: 1.6,
                  }}
                >
                  Enter the 6-digit code from your authenticator app.
                </Typography>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: reduxTheme.neutral_colors?.[100] || "#333",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    2FA Code
                  </Typography>
                  <TextField
                    placeholder="Enter 6-digit code"
                    variant="outlined"
                    fullWidth
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    onFocus={() => setFocusedField("totpCode")}
                    onBlur={() => setFocusedField(null)}
                    disabled={isLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        transition: "all 0.3s ease",
                        height: "56px",
                        "& fieldset": {
                          borderColor:
                            reduxTheme.neutral_colors?.[300] ||
                            "rgba(0,0,0,0.1)",
                          borderWidth: "2px",
                        },
                        "&:hover fieldset": {
                          borderColor:
                            reduxTheme.neutral_colors?.[400] ||
                            "rgba(194, 147, 99, 0.5)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 25px rgba(${
                            reduxTheme.primary_colors?.[400]
                              ? parseInt(
                                  reduxTheme.primary_colors[400].slice(1, 3),
                                  16
                                )
                              : 97
                          }, ${
                            reduxTheme.primary_colors?.[400]
                              ? parseInt(
                                  reduxTheme.primary_colors[400].slice(3, 5),
                                  16
                                )
                              : 68
                          }, ${
                            reduxTheme.primary_colors?.[400]
                              ? parseInt(
                                  reduxTheme.primary_colors[400].slice(5, 7),
                                  16
                                )
                              : 56
                          }, 0.15)`,
                          "& fieldset": {
                            borderColor:
                              reduxTheme.primary_colors?.[400] || "#614438",
                          },
                        },
                      },
                      "& .MuiInputBase-input": {
                        color: reduxTheme.neutral_colors?.[100] || "#333",
                        fontSize: "16px",
                      },
                    }}
                  />
                </Box>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    mb: 3,
                  }}
                >
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[300] || "#C29363"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#614438"
                      } 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontSize: 14,
                    }}
                  >
                    <motion.span whileHover={{ scale: 1.05 }}>
                      Back to Login
                    </motion.span>
                  </Link>
                </Box>
              </motion.div>
              <motion.div variants={itemVariants}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    background: `linear-gradient(135deg, ${
                      reduxTheme.primary_colors?.[400] || "#614438"
                    } 0%, ${
                      reduxTheme.primary_colors?.[300] || "#C29363"
                    } 100%)`,
                    textTransform: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    mb: 3,
                    fontSize: "16px",
                    fontWeight: 600,
                    boxShadow: `0 8px 25px rgba(${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(1, 3),
                            16
                          )
                        : 97
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(3, 5),
                            16
                          )
                        : 68
                    }, ${
                      reduxTheme.primary_colors?.[400]
                        ? parseInt(
                            reduxTheme.primary_colors[400].slice(5, 7),
                            16
                          )
                        : 56
                    }, 0.3)`,
                    border: "none",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${
                        reduxTheme.primary_colors?.[500] || "#503629"
                      } 0%, ${
                        reduxTheme.primary_colors?.[400] || "#614438"
                      } 100%)`,
                      boxShadow: `0 12px 35px rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.4)`,
                    },
                    "&:disabled": {
                      background: `rgba(${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(1, 3),
                              16
                            )
                          : 97
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(3, 5),
                              16
                            )
                          : 68
                      }, ${
                        reduxTheme.primary_colors?.[400]
                          ? parseInt(
                              reduxTheme.primary_colors[400].slice(5, 7),
                              16
                            )
                          : 56
                      }, 0.3)`,
                    },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isLoading ? (
                    <CircularProgress
                      size={24}
                      sx={{ color: reduxTheme.neutral_colors?.[100] || "#fff" }}
                    />
                  ) : (
                    "Verify 2FA"
                  )}
                </Button>
              </motion.div>
            </Box>
          </Paper>
        </motion.div>
      </Grid>
    </Grid>
  );
};

export default Verify2FA;
